import type {JSX} from 'react';
import {useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {Button, DatePicker, Input, Select} from '@/components';
import {Filter} from '@/components/Filter';
import DownloadTransactionCSVButton from '@/components/transactions/DownloadTransactionCSVButton';
import {
  getTransactionChannelsOptions,
  getTransactionProcessorsOptions,
  getTransactionStatusOptions,
  getTransactionTypesOptions,
} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import type {TransactionFiltersProps} from '@/screens/Transaction/List/types';
import type {AfriexTransactionStatus} from '@/types';
import {mapCountryListToCurrencyOptions} from '@/utils/countries';

export default function TransactionFilters({
  transactions,
  exportFilters,
  filterCount,
  currentFilters,
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  selectCurrentFilters,
}: TransactionFiltersProps): JSX.Element {
  const {userId} = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexTransactionStatus;
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const currencies = mapCountryListToCurrencyOptions(countryList);

  const fromDate = filtersToApply.fromDate
    ? new Date(filtersToApply.fromDate)
    : undefined;

  const toDate = filtersToApply.toDate
    ? new Date(filtersToApply.toDate)
    : undefined;

  const handleShowFilterChange = (): void => {
    setShowFilters(!showFilters);
  };

  const handleApplyFilter = (): void => {
    // Only close on success. Dismissing the panel after a rejected date range
    // hid the values the user has to correct.
    if (handleApplyFilters()) {
      setShowFilters(false);
    }
  };

  const handleClearFilters = (): void => {
    clearFilters();
  };

  return (
    <div className="mt-5 w-full">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Filter Box */}
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={handleShowFilterChange}
            label="Filters"
            data-cy="admin-transactions-filter-toggle"
            positionLeft={1}
            bodyClassName="w-full md:w-[600px]"
          >
            {/* Filter Body */}
            {showFilters && (
              <div
                className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full"
                data-cy="admin-transactions-filter-panel"
              >
                <div className="flex flex-col gap-4">
                  {/* First Row */}
                  <div className="flex flex-col md:flex-row items-center gap-2">
                    <Input
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        selectAppliedFilters('transactionId', e.target.value)
                      }
                      name="transactionId"
                      label="Transaction ID"
                      type="text"
                      value={filtersToApply.transactionId ?? ''}
                      className="w-full isFull"
                      placeholder="Enter Transaction ID"
                      data-cy="admin-transactions-filter-transaction-id"
                    />
                    {!userId && (
                      <Input
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          selectAppliedFilters('userId', e.target.value)
                        }
                        name="userId"
                        label="User ID"
                        type="text"
                        value={filtersToApply.userId ?? ''}
                        className="w-full isFull"
                        placeholder="Enter User ID"
                        data-cy="admin-transactions-filter-user-id"
                      />
                    )}
                  </div>

                  {/* Second Row */}
                  <div className="flex flex-col md:flex-row gap-2">
                    {/* react-datepicker only forwards a whitelist of props to its
                        input, so data-cy goes on a wrapper and the page object
                        reaches the input through it. */}
                    <div data-cy="admin-transactions-filter-from-date">
                      <DatePicker
                        label="From Date"
                        selectedDate={fromDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('fromDate', date)
                        }
                        // Constrain each picker by the other, so an invalid
                        // range cannot be selected at all. Apply still rejects
                        // one that was typed rather than picked.
                        maxDate={toDate}
                        placeholderText="Select Date"
                      />
                    </div>
                    <div data-cy="admin-transactions-filter-to-date">
                      <DatePicker
                        label="To Date"
                        selectedDate={toDate}
                        setSelectedDate={(date) =>
                          selectAppliedFilters('toDate', date)
                        }
                        minDate={fromDate}
                        placeholderText="Select Date"
                      />
                    </div>
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('channel', e.target.value)
                      }
                      value={filtersToApply.channel}
                      name="channel"
                      options={getTransactionChannelsOptions()}
                      placeholder="Select"
                      label="Payment Method"
                      data-cy="admin-transactions-filter-channel"
                    />
                  </div>

                  {/* Third Row */}
                  <div className="flex flex-col md:flex-row gap-2">
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('type', e.target.value)
                      }
                      value={filtersToApply.type}
                      name="type"
                      options={getTransactionTypesOptions()}
                      placeholder="Select"
                      label="Filter by Type"
                      data-cy="admin-transactions-filter-type"
                    />
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('processor', e.target.value)
                      }
                      value={filtersToApply.processor}
                      name="processor"
                      options={getTransactionProcessorsOptions()}
                      placeholder="Select"
                      label="Filter by Processor"
                      data-cy="admin-transactions-filter-processor"
                    />
                    <Select
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        selectAppliedFilters('currency', e.target.value)
                      }
                      value={filtersToApply.currency}
                      name="currency"
                      options={currencies}
                      placeholder="Select"
                      label="Filter by Currency"
                      data-cy="admin-transactions-filter-currency"
                    />
                  </div>
                </div>

                <div className="flex md:justify-end">
                  <Button
                    onClick={handleClearFilters}
                    colorScheme="cyan"
                    variant="ghost"
                    data-cy="admin-transactions-filter-clear"
                  >
                    Clear
                  </Button>

                  <Button
                    onClick={handleApplyFilter}
                    colorScheme="cyan"
                    disabled={filterCount === 0}
                    className="ml-3"
                    data-cy="admin-transactions-filter-apply"
                  >
                    Apply Filters {filterCount && ` (${filterCount})`}
                  </Button>
                </div>
              </div>
            )}
          </Filter>
        </div>

        {/* Status Select Box */}
        <div className="flex flex-wrap gap-3 items-center md:space-x-3">
          {!status && (
            <Select
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                selectCurrentFilters('status', e.target.value)
              }
              value={currentFilters.status}
              name="status"
              options={getTransactionStatusOptions()}
              placeholder="Filter by Status"
              data-cy="admin-transactions-filter-status"
            />
          )}

          <DownloadTransactionCSVButton
            filters={exportFilters}
            userId={userId}
            disabled={!transactions.length}
          />
        </div>
      </div>
    </div>
  );
}
